import { PeriodControl, VatControl } from "../components/Controls";
import { MrrChart } from "../components/LazyCharts";
import { AppIcon, DeltaChip, EmptyState, KpiCard, ProviderSplitCard } from "../components/ui";
import { ConcentrationCard, ContributionCard, GrowthCard, RpiKpi } from "../components/insights";
import { ratingShort } from "../lib/insights";
import { eur, int } from "../lib/format";
import { usePrefs } from "../lib/prefs";
import { Link } from "../lib/router";
import type { OverviewResponse } from "@apphq/shared/types";

/** SCREENS §7 phone layout of the overview: same data as the desktop screen, tailored presentation. */
export function OverviewMobileTop() {
  return (
    <>
      <h1 className="m-title">Overzicht</h1>
      <div className="m-glass-bar glass">
        <PeriodControl />
      </div>
      <div className="m-filters">
        <VatControl />
      </div>
    </>
  );
}

export function OverviewMobileBody({ d }: { d: OverviewResponse }) {
  const { env } = usePrefs();
  return (
    <>
      <section className="card m-mrr">
        <div className="kpi-label">MRR</div>
        <div className="m-mrr-row">
          <span className="big-number">{eur(d.kpis.mrr.value)}</span>
          <DeltaChip value={d.kpis.mrr.value} previous={d.kpis.mrr.previous} />
        </div>
        {d.mrr_series.length >= 2 ? (
          <MrrChart points={d.mrr_series} height={150} />
        ) : (
          <p className="muted tight">{env === "sandbox" ? "Geen trend voor sandbox." : "De trendlijn verschijnt vanaf de tweede dag."}</p>
        )}
      </section>

      <div className="m-kpi-hero">
        <KpiCard label="Omzet (periode)" value={eur(d.kpis.revenue.value)} previous={{ current: d.kpis.revenue.value, before: d.kpis.revenue.previous }} />
      </div>

      {/* 4 tiles = 2 even rows; an odd last tile spans the full width (see .m-kpi-grid) so there is never a blank cell */}
      <div className="m-kpi-grid">
        <KpiCard label="Op telefoon" value={int(d.kpis.active_on_phone.value)} previous={{ current: d.kpis.active_on_phone.value, before: d.kpis.active_on_phone.previous }} />
        <KpiCard label="Abonnees" value={int(d.kpis.active_subscriptions.value)} previous={{ current: d.kpis.active_subscriptions.value, before: d.kpis.active_subscriptions.previous }} />
        <KpiCard label="Trials" value={int(d.kpis.active_trials.value)} previous={{ current: d.kpis.active_trials.value, before: d.kpis.active_trials.previous }} />
        <RpiKpi rpi={d.rpi} />
      </div>

      <GrowthCard deltas={d.deltas} />
      <ContributionCard contribution={d.contribution} movers={d.movers} collapsible />
      <ConcentrationCard c={d.concentration} />

      <ProviderSplitCard split={d.revenue_by_provider} />

      <section className="card m-apps">
        <h2 className="m-section">Apps</h2>
        {d.apps.length === 0 ? (
          <EmptyState title="Nog geen apps">Apps uit App Store Connect worden automatisch gekoppeld.</EmptyState>
        ) : (
          <ul className="m-app-list">
            {d.apps.map((a) => (
              <li key={a.id}>
                <Link to={`/apps/${encodeURIComponent(a.id)}`} className="m-app-row">
                  <AppIcon name={a.name} url={a.icon_url} size={40} seed={a.id} />
                  <span className="m-app-main">
                    <strong>{a.name}</strong>
                    <span className="muted small">
                      {int(a.subscribers)} abonnees · {int(a.active_on_phone.value)} op telefoon{a.rating?.avg ? ` · ${ratingShort(a.rating)}` : ""}
                    </span>
                  </span>
                  <span className="m-app-mrr">{eur(a.revenue)}</span>
                  <span className="m-chevron" aria-hidden>
                    ›
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </>
  );
}
